import { RowDragEvent, IRowNode, GridApi } from 'ag-grid-community'
import { RefObject, useCallback, useMemo, useState } from 'react'
import { TreeRow } from '../../model'
import { AUTO_COLUMN_ID } from '../../../BulkSheet/const'
import { getParentUuid } from '../../lib/tree'

// Set hovered information to context. Change styles using cellClassRules of colDefs
export const useDragTreeStyle = <R extends TreeRow>(
  ref: RefObject<HTMLDivElement>,
  acceptChild: (parent: R, child: R) => boolean,
  gridApi: GridApi | undefined,
  updateContext: (partial: Record<string, any>) => void
) => {
  const [topLevelNodes, setTopLevelNodes] = useState<IRowNode[]>([])

  const onRowDragEnter = useCallback((event: RowDragEvent) => {
    const ids = event.nodes.map(v => v.id)
    setTopLevelNodes(event.nodes.filter(n => !ids.includes(n.parent?.id)))
  }, [])

  const onAutoColumn = useCallback((event: RowDragEvent): boolean => {
    const autoGroupColumn = event.api.getColumn(AUTO_COLUMN_ID)
    if (!autoGroupColumn) return false
    const marginLeft = ref.current?.offsetLeft || 0
    return (
      (autoGroupColumn.getLeft() || 0) + marginLeft < event.event.x &&
      (autoGroupColumn.getRight() || 0) + marginLeft > event.event.x
    )
  }, [])

  const refreshRows = useCallback(
    (params: { draggableNodeId?: string; onTree?: boolean }) => {
      updateContext(params)
      gridApi?.refreshCells()
    },
    // v32対応: topLevelNodes は関数本体で未使用なので削除
    [gridApi, updateContext]
  )

  const onRowDragMove = useCallback(
    (event: RowDragEvent) => {
      const overNode = event.overNode
      if (!overNode || !overNode.id) return

      // Can move to same level
      const onTree = onAutoColumn(event)
      if (
        !onTree &&
        topLevelNodes.every(
          v =>
            v.level === overNode.level &&
            v.id !== overNode.id &&
            getParentUuid(v.data) === getParentUuid(overNode.data)
        )
      ) {
        refreshRows({ draggableNodeId: overNode.id, onTree: false })
        return
      }

      // Can move to another parent
      const moveToDecendants = topLevelNodes.some(movingRow =>
        overNode.data.treeValue.includes(movingRow.data.uuid)
      )
      if (
        onTree &&
        topLevelNodes.every(v => acceptChild(overNode.data, v.data)) &&
        !topLevelNodes.some(v => v.id === overNode.id) &&
        !topLevelNodes.some(v => getParentUuid(v.data) === overNode.id) &&
        !moveToDecendants
      ) {
        refreshRows({ draggableNodeId: overNode.id, onTree: true })
        return
      }

      // Clear
      refreshRows({ draggableNodeId: undefined, onTree: false })
    },
    // v32対応: 関数本体で使っている onAutoColumn, refreshRows, acceptChild を追加
    // これにより gridApi が有効になった時点で refreshRows が再生成 → onRowDragMove も再生成される
    [topLevelNodes, onAutoColumn, refreshRows, acceptChild]
  )

  const refreshDragStyle = useCallback(() => {
    refreshRows({ draggableNodeId: undefined, onTree: false })
  }, [refreshRows]) // v32対応: refreshRows を追加

  return useMemo(
    () => ({ onRowDragEnter, onRowDragMove, refreshDragStyle }),
    [onRowDragEnter, onRowDragMove, refreshDragStyle]
  )
}
